import { useState, useEffect } from "react";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Coins,
  TrendingUp,
  Users,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Euro,
  FileText,
} from "lucide-react";

const CommissionsPage = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rates, setRates] = useState([]);
  const [users, setUsers] = useState([]);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    user_id: "",
    taux_ventes: 5,
    taux_sav: 3,
    actif: true
  });
  const [editingRate, setEditingRate] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, ratesRes, usersRes] = await Promise.all([
        API.get("/commissions/summary", { params: { period } }),
        user?.role === "admin" ? API.get("/commissions/rates") : Promise.resolve({ data: [] }),
        user?.role === "admin" ? API.get("/users") : Promise.resolve({ data: [] })
      ]);
      setSummary(summaryRes.data);
      setRates(ratesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRate) {
        await API.put(`/commissions/rates/${editingRate.user_id}`, {
          taux_ventes: formData.taux_ventes,
          taux_sav: formData.taux_sav,
          actif: formData.actif
        });
        toast.success("Taux de commission mis à jour");
      } else {
        await API.post("/commissions/rates", formData);
        toast.success("Taux de commission créé");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm("Supprimer ce taux de commission ?")) return;
    try {
      await API.delete(`/commissions/rates/${userId}`);
      toast.success("Taux de commission supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const openEditDialog = (rate) => {
    setEditingRate(rate);
    setFormData({
      user_id: rate.user_id,
      taux_ventes: rate.taux_ventes,
      taux_sav: rate.taux_sav,
      actif: rate.actif
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRate(null);
    setFormData({
      user_id: "",
      taux_ventes: 5,
      taux_sav: 3,
      actif: true
    });
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const response = await API.get(`/commissions/export/pdf?period=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commissions_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export PDF téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExporting(false);
    }
  };

  // Get users without rates for the select
  const usersWithoutRates = users.filter(
    u => !rates.some(r => r.user_id === u.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Commissions</h1>
          <p className="text-muted-foreground">Suivi des commissions sur les ventes</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Cette semaine</SelectItem>
              <SelectItem value="month">Ce mois</SelectItem>
              <SelectItem value="year">Cette année</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Export Buttons */}
          <Button variant="outline" onClick={exportToPDF} disabled={exporting} data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                <Euro className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total ventes</p>
                <p className="text-2xl font-bold">{(summary?.totaux?.ventes || 0).toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <Coins className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total commissions</p>
                <p className="text-2xl font-bold text-primary">{(summary?.totaux?.commissions || 0).toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Nombre de ventes</p>
                <p className="text-2xl font-bold">{summary?.totaux?.nombre_ventes || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="summary" className="space-y-4">
        <TabsList>
          <TabsTrigger value="summary">Récapitulatif</TabsTrigger>
          {user?.role === "admin" && (
            <TabsTrigger value="rates">Taux de commission</TabsTrigger>
          )}
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Commissions par vendeur
              </CardTitle>
              <CardDescription>
                Répartition des commissions sur la période sélectionnée
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.par_vendeur?.length > 0 ? (
                <div className="space-y-4">
                  {summary.par_vendeur.map((vendeur, index) => {
                    const maxCommission = Math.max(...summary.par_vendeur.map(v => v.total_commissions));
                    const percentage = maxCommission > 0 ? (vendeur.total_commissions / maxCommission) * 100 : 0;
                    return (
                      <div key={vendeur._id || index} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{vendeur.vendeur_nom || "Vendeur"}</p>
                              <p className="text-sm text-muted-foreground">
                                {vendeur.nombre_ventes} vente(s) - {vendeur.total_ventes?.toFixed(2)}€ CA
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary">{vendeur.total_commissions?.toFixed(2)}€</p>
                            <p className="text-xs text-muted-foreground">commission</p>
                          </div>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Coins className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="font-medium">Aucune commission sur cette période</p>
                  <p className="text-sm text-muted-foreground">Les commissions seront calculées automatiquement sur les ventes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rates Tab (Admin only) */}
        {user?.role === "admin" && (
          <TabsContent value="rates">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Taux de commission personnalisés</CardTitle>
                  <CardDescription>
                    Définissez des taux spécifiques par utilisateur (remplace les taux par défaut)
                  </CardDescription>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-rate-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Nouveau taux
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <form onSubmit={handleSubmit}>
                      <DialogHeader>
                        <DialogTitle>{editingRate ? "Modifier le taux" : "Nouveau taux de commission"}</DialogTitle>
                        <DialogDescription>
                          Définissez les taux de commission pour un utilisateur
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        {!editingRate && (
                          <div className="space-y-2">
                            <Label>Utilisateur</Label>
                            <Select
                              value={formData.user_id}
                              onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                              required
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner un utilisateur" />
                              </SelectTrigger>
                              <SelectContent>
                                {usersWithoutRates.map((u) => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.prenom} {u.nom} ({u.role})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Taux ventes boutique (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              max="100"
                              value={formData.taux_ventes}
                              onChange={(e) => setFormData({ ...formData, taux_ventes: parseFloat(e.target.value) })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Taux SAV (%)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              max="100"
                              value={formData.taux_sav}
                              onChange={(e) => setFormData({ ...formData, taux_sav: parseFloat(e.target.value) })}
                            />
                          </div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                          Annuler
                        </Button>
                        <Button type="submit">
                          {editingRate ? "Enregistrer" : "Créer"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {rates.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Utilisateur</TableHead>
                        <TableHead>Rôle</TableHead>
                        <TableHead className="text-center">Taux ventes</TableHead>
                        <TableHead className="text-center">Taux SAV</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rates.map((rate) => (
                        <TableRow key={rate.user_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
                                {rate.user?.prenom?.[0]}{rate.user?.nom?.[0]}
                              </div>
                              <span className="font-medium">{rate.user?.prenom} {rate.user?.nom}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="capitalize">{rate.user?.role}</Badge>
                          </TableCell>
                          <TableCell className="text-center font-mono font-medium text-green-600">{rate.taux_ventes}%</TableCell>
                          <TableCell className="text-center font-mono font-medium text-blue-600">{rate.taux_sav}%</TableCell>
                          <TableCell>
                            <Badge variant={rate.actif ? "default" : "secondary"}>
                              {rate.actif ? "Actif" : "Inactif"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(rate)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(rate.user_id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Coins className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="font-medium">Aucun taux personnalisé</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Les taux par défaut sont appliqués à tous les utilisateurs.
                      <br />
                      Vous pouvez les modifier dans <strong>Paramètres</strong>.
                    </p>
                    <Button variant="outline" onClick={() => window.location.href = '/settings'}>
                      Modifier les taux par défaut
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

      </Tabs>
    </div>
  );
};

export default CommissionsPage;
